export type AspectRatio = '16:9' | '9:16' | '1:1' | '4:3' | '3:4' | '4:5';
export type ImageQuality = '1K' | '2K' | '4K';

export interface StyleReference {
  image: string;
  description: string;
}

export type StyleSnapshot = Pick<
  StyleSettings,
  'presetId' | 'customStyle' | 'aspectRatio' | 'imageQuality' | 'culturalGuidelines'
>;

export interface Scene {
  id: string;
  episodeUuid: string;
  order: number;
  prompt: string;
  references: { sceneIds: string[]; castIds: string[] };
  imageIds: string[];
  videoIds: string[];
  heroImageId: string | null;
  status: 'empty' | 'generating' | 'ready' | 'error';
  version: number;
  createdAt: number;
  updatedAt: number;
  deletedAt: number | null;
}

export interface SceneImage {
  id: string;
  sceneId: string;
  path: string;                       // relative path; mediaUrl() prepends /api/episode/{uuid}/media/
  prompt: string;                     // user prompt at gen time (NOT the resolved/composed prompt)
  references: { sceneIds: string[]; castIds: string[] };  // snapshot at gen time — variant is self-describing
  fresh: boolean;                     // was Fresh-prompt toggle ON?
  style: StyleSnapshot;
  version: number;
  createdAt: number;
  deletedAt: number | null;
}

export interface SceneVideo {
  id: string;
  sceneId: string;
  sourceImageId: string;
  extendsVideoId: string | null;
  path: string;
  prompt: string;
  references: { sceneIds: string[]; castIds: string[] };
  fresh: boolean;
  duration: 4 | 6 | 8;
  model: 'veo-fast' | 'veo-full';
  status: 'generating' | 'ready' | 'error';
  veoVideoObject: unknown;
  version: number;
  createdAt: number;
  deletedAt: number | null;
}

export interface Character {
  id: string;
  name: string;
  description: string;
  type: 'character' | 'prop' | 'location' | 'other';
  imagePath: string;
  prompt?: string;
}

export interface StyleSettings {
  presetId: string | null;
  customStyle?: StyleReference;
  aspectRatio: AspectRatio;
  imageQuality: ImageQuality;
  culturalGuidelines: boolean;
  // null on either field = "use the system default for that kind" — see
  // PromptComposer in the backend. Set to a custom episode-scoped template
  // ID when the user has cloned and edited.
  activeImageTemplateId: string | null;
  activeVideoTemplateId: string | null;
}

export interface StylePreset {
  id: string;
  name: string;
  description: string;
  exampleImageUrl?: string;
  styleReference?: StyleReference;
  defaultAspectRatio?: AspectRatio;
  imageModel: string;
  videoModel: string;
  isDefault: boolean;
}

export type TemplateKind = 'image' | 'video';

export interface PromptTemplate {
  id: string;
  scope: 'global' | 'episode';
  episodeUuid?: string | null;
  kind: TemplateKind;
  // System rows (the two fixed-UUID defaults) are read-only — UI hides
  // edit/delete buttons for these and the backend rejects mutations.
  isSystem: boolean;
  clonedFromId: string | null;
  name: string;
  body: string;
}

/**
 * Fixed UUIDs shared with the backend (PromptTemplate::SYSTEM_IMAGE_ID /
 * SYSTEM_VIDEO_ID). The frontend uses these to detect "this is the default"
 * without an extra round trip.
 */
export const SYSTEM_IMAGE_TEMPLATE_ID = '00000000-0000-0000-0000-000000000001';
export const SYSTEM_VIDEO_TEMPLATE_ID = '00000000-0000-0000-0000-000000000002';

export interface Episode {
  uuid: string;
  title: string;
  script: string;
  styleSettings: StyleSettings;
}

export type SaveStatus =
  | { kind: 'idle'; lastSavedAt: number | null }
  | { kind: 'saving'; since: number }
  | { kind: 'queued'; count: number }
  | { kind: 'error'; message: string };

export interface QueuedMutation {
  id: string;
  episodeUuid: string;
  endpoint: string;
  method: 'POST' | 'PATCH' | 'DELETE';
  body: unknown;
  expectedVersion?: number;
  createdAt: number;
  retryCount: number;
}
